import { createSelector } from "reselect"
import { get, chain, map, filter, orderBy, first, find, mergeWith, isNumber, some, isEmpty, size } from "lodash"
import { getTickerAndMicFromContributor } from "helpers/securities"
import { computeSortedContributors, positionSetContributorsSummaryText, mapPerformanceContributorsReturnsToValues, mapPerformanceFactorsReturnsToValue, createContributorFilter } from "helpers/contributors"
import { getAllAvailableRollups } from "helpers/rollups"
import { getViewType, getPortfolioName, getShowNotHeld, getMustAggregateFactors } from "modules/portfolio-root/selectors/portfolios"
import { getModelFactorsClassification } from "selectors/modelMetadata"
import { getTagCategories, getAttributeCategories } from "modules/portfolio-root/selectors"
import { getSelectedViewsFactors } from "selectors/factors"
import { computeGroupedContributorsCSV, computeContributorsGroupCSV, computeContributorsCSV } from "helpers/csv"
import { aggregateFactorsSumBy } from "helpers/factorClassification/index"
import { computeFactorCategories } from "helpers/factors"
import { computeSelectedGroup } from "helpers/analyze"
import { VIEW_TYPES } from "helpers/base"
import { METRIC_TYPES } from "helpers/assets/constants"
import { makeGroupSort } from "helpers/contributorsSort"

const EMPTY_ARRAY = []
const EMPTY_OBJECT = {}

function getFactors(factors, mustAggregateFactors, selectedViewsFactors, factorsClassification) {
  if (!mustAggregateFactors) {
    return factors
  }
  const result = aggregateFactorsSumBy(factorsClassification, factors, "value")

  if (isEmpty(selectedViewsFactors)) {
    return result
  }

  return chain(factors)
    .filter(factor => some(selectedViewsFactors, item => item.id === factor.id))
    .concat(result)
    .value()
}

function computeNotHeldBucket(contributors, mustAggregateFactors, selectedViews, selectedViewsFactors, factorsClassification, performanceMetricField) {
  const merge = (obj, source) => mergeWith(obj, source, (objValue, srcValue) =>
    !objValue ? srcValue : objValue + srcValue,
  )

  return chain(contributors)
    .filter(item => !!item.notHeld)
    .reduce((acc, value) => {
      acc.size += value.averagePercentEquity
      acc.total += value.total
      acc.factorsTotal += get(value, ["attribution", "summary", "factorsReturn", performanceMetricField])
      acc.alpha += get(value, ["attribution", "summary", "specificReturn", performanceMetricField])
      acc.trading += get(value, ["attribution", "summary", "tradingReturn", performanceMetricField])
      const residualValue = get(value, ["attribution", "summary", "residualReturn", performanceMetricField])
      if (residualValue !== undefined) {
        acc.residual = (acc.residual || 0) + residualValue
      }

      const factors = getFactors(value.attribution.factors, mustAggregateFactors, selectedViewsFactors, factorsClassification)

      merge(acc.factors, chain(factors)
        .keyBy("id")
        .mapValues(f => f.value)
        .value())

      const factorCategories = computeFactorCategories(factors, selectedViews)
      merge(acc.categories, factorCategories)

      return acc
    }, {
      id: "NOT HELD",
      notHeld: true,
      size: 0,
      total: 0,
      factorsTotal: 0,
      alpha: 0,
      trading: 0,
      factors: {},
      categories: {},
    })
    .value()
}

function mapPerformanceContributors(contributors, showNotHeld, mustAggregateFactors, selectedViews, selectedViewsFactors, factorsClassification, performanceMetricField) {
  const result = chain(contributors)
    .filter(item => showNotHeld || !item.notHeld)
    .map(security => {
      const { ticker, mic: exchange } = getTickerAndMicFromContributor(security)
      const factors = getFactors(security.attribution.factors, mustAggregateFactors, selectedViewsFactors, factorsClassification)
      const residualValue = get(security, ["attribution", "summary", "residualReturn", performanceMetricField])

      return {
        id: security.id,
        modelProviderId: security.modelProviderId,
        size: security.averagePercentEquity,
        total: get(security, ["totalReturn", performanceMetricField]),
        country: security.country,
        classification: get(security, "classification.name"),
        currency: security.currency,
        notHeld: security.notHeld,
        assetClass: security.assetClass,
        assetSubclass: security.assetSubclass,
        description: security.description,
        isUnmodeled: security.isUnmodeled,
        bloombergTicker: security.bloombergTicker,
        factorsTotal: get(security, ["attribution", "summary", "factorsReturn", performanceMetricField]),
        alpha: get(security, ["attribution", "summary", "specificReturn", performanceMetricField]),
        trading: get(security, ["attribution", "summary", "tradingReturn", performanceMetricField]),
        ...residualValue !== undefined && { residual: residualValue },
        factors: chain(factors)
          .keyBy("id")
          .mapValues(factor => get(factor, "value"))
          .value(),
        categories: computeFactorCategories(factors, selectedViews),
        ticker,
        exchange,
      }
    })
    .value()

  if (!showNotHeld) {
    result.push(computeNotHeldBucket(contributors, mustAggregateFactors, selectedViews, selectedViewsFactors, factorsClassification, performanceMetricField))
  }
  return result
}

export const getGroupBy = (state, props) => get(props, "contributorsGrouping.id")

export const getIncludeNotHeld = createSelector(
  [getViewType],
  (viewType) => viewType === VIEW_TYPES.ACTIVE,
)

const getRawContributorsData = state => get(state, "global.data.performanceContributors.data.model.simulation.performanceContributors")
const getSelected = state => get(state, "modules.analyzePerformanceDetails.selected", EMPTY_OBJECT)
const getOrder = state => get(state, "modules.analyzePerformanceDetails.order", EMPTY_OBJECT)
const getRawGroupedContributorsData = state => get(state, "global.data.groupedPerformanceContributors.data.model.simulation.groupedPerformanceContributors")
const getContributorsGrouping = (state, props) => get(props, "contributorsGrouping")
const getRawSelectedGroup = (state, props) => get(props, "selectedGroup", EMPTY_OBJECT)
const getClassifications = (state) => get(state, "global.data.classifications.data.classifications")
const getSelectedDate = (state, props) => props.selectedDate
const getSelectedViews = (state, props) => props.selectedViews
export const getMetricType = (state, props) => props.metricType

const getPerformanceMetricField = createSelector(
  [getMetricType],
  (metricType) => metricType === METRIC_TYPES.CURRENCY_PERFORMANCE ? "cumulativeCurrency" : "cumulativePercent",
)

const getSelectedField = createSelector(
  [getSelected],
  (selected) => get(selected, "field", "total"),
)

const getGroupedContributorsData = createSelector(
  [getRawGroupedContributorsData, getPerformanceMetricField],
  (groupedContributors, performanceMetricField) => map(groupedContributors, group => ({
    ...group,
    attribution: {
      ...group.attribution,
      factors: mapPerformanceFactorsReturnsToValue(group.attribution.factors, performanceMetricField),
    },
    contributors: mapPerformanceContributorsReturnsToValues(group.contributors, performanceMetricField),
  })),
)

export const getSelectedGroup = createSelector(
  [getRawSelectedGroup, getGroupedContributorsData],
  (rawSelectedGroup, groupedData) => computeSelectedGroup(rawSelectedGroup, groupedData, "averagePercentEquity"),
)

const getContributorsData = createSelector(
  [getRawContributorsData, getPerformanceMetricField],
  mapPerformanceContributorsReturnsToValues,
)

const getContributors = createSelector(
  [getContributorsData, getShowNotHeld, getMustAggregateFactors, getSelectedViews, getSelectedViewsFactors, getModelFactorsClassification, getPerformanceMetricField],
  mapPerformanceContributors,
)

const getFilteredContributors = createSelector(
  [getContributors, getSelectedField],
  (data, selectedField) => filter(data, createContributorFilter(selectedField)),
)

export const getSortedContributors = createSelector(
  [getFilteredContributors, getSelectedField, getOrder],
  computeSortedContributors,
)

const getGroupedContributors = createSelector(
  [getGroupedContributorsData, getShowNotHeld, getMustAggregateFactors, getSelectedViews, getSelectedViewsFactors, getModelFactorsClassification, getPerformanceMetricField],
  (groups, showNotHeld, mustAggregateFactors, selectedViews, selectedViewsFactors, factorsClassification, performanceMetricField) => map(groups, group => {
    const factors = getFactors(group.attribution.factors, mustAggregateFactors, selectedViewsFactors, factorsClassification)
    const residualValue = get(group, ["attribution", "summary", "residualReturn", performanceMetricField])
    return {
      ...group,
      total: get(group, ["totalReturn", performanceMetricField]),
      factorsTotal: get(group, ["attribution", "summary", "factorsReturn", performanceMetricField]),
      alpha: get(group, ["attribution", "summary", "specificReturn", performanceMetricField]),
      trading: get(group, ["attribution", "summary", "tradingReturn", performanceMetricField]),
      ...residualValue !== undefined && { residual: residualValue },
      factors: chain(factors)
        .keyBy("id")
        .mapValues(f => f.value)
        .value(),
      categories: computeFactorCategories(factors, selectedViews),
      totalPercentEquity: group.averagePercentEquity,
      contributors: mapPerformanceContributors(group.contributors, showNotHeld, mustAggregateFactors, selectedViews, selectedViewsFactors, factorsClassification, performanceMetricField),
    }
  }),
)

const getFilteredGroupedContributors = createSelector(
  [getGroupedContributors, getSelectedField, getSelectedGroup],
  (groupedContributors, selectedField, selectedGroup) => {
    const groupFilter = selectedGroup ? group => selectedGroup.id === group.id : createContributorFilter(selectedField)
    return chain(groupedContributors)
      .filter(groupFilter)
      .map(group => ({
        ...group,
        contributors: filter(group.contributors, createContributorFilter(selectedField)),
      }))
      .value()
  },
)

export const getSortedGroupContributors = createSelector(
  [getFilteredGroupedContributors, getSelectedField, getOrder],
  (data, selectedField, order) => {
    if (data.length === 0) return EMPTY_ARRAY

    return chain(data)
      .map(group => ({
        ...group,
        value: get(group, selectedField) * 100,
        contributors: orderBy(group.contributors, [
          (security) => {
            switch (order.by) {
              case "name":
                return security.id
              case "equity":
                return order.abs ? Math.abs(security.size) : security.size
              default: {
                const value = get(security, selectedField)
                return order.abs && isNumber(value) ? Math.abs(value) : value
              }
            }
          },
        ], [order.dir]),
      }))
      .orderBy(...makeGroupSort(order))
      .value()
  },
)

const getAccountRollups = createSelector(
  [getTagCategories, getClassifications, getAttributeCategories],
  (tagCategories, classifications, attributeCategories) => ({
    tagCategories,
    classifications,
    attributeCategories,
  }),
)
export const getAvailableRollups = createSelector(
  [getAccountRollups, getSelectedDate, getViewType],
  getAllAvailableRollups,
)

export const getContributorsSummaryText = createSelector(
  [getContributorsGrouping, getSortedContributors, getSortedGroupContributors, getSelectedGroup, getShowNotHeld, getViewType],
  positionSetContributorsSummaryText,
)

export const getCsvData = createSelector(
  [getSortedContributors, getSortedGroupContributors, getSelected, getContributorsGrouping, getPortfolioName, getSelectedDate, getViewType, getSelectedGroup, getMetricType],
  (data, groupedData, selected, contributorsGrouping, portfolioName, selectedDate, viewType, selectedGroup, metricType) => {
    // net controls the format for returns
    const net = metricType === METRIC_TYPES.CURRENCY_PERFORMANCE

    if (selectedGroup && size(groupedData) > 0) {
      const group = first(groupedData)
      return computeGroupedContributorsCSV({
        type: "Performance",
        portfolioName,
        selected,
        selectedDate,
        viewType,
        group,
        net,
      })
    }

    if (contributorsGrouping) {
      return computeContributorsGroupCSV({
        type: "Performance",
        portfolioName,
        selected,
        selectedDate,
        viewType,
        contributorsGrouping,
        data: groupedData,
        net,
      })
    }

    return computeContributorsCSV({
      type: "Performance",
      portfolioName,
      selected,
      selectedDate,
      viewType,
      data,
      net,
    })
  },
)

export const getSelectedGroupValue = createSelector(
  [getSelectedGroup, getGroupedContributors, getSelectedField],
  (selectedGroup, groupedContributors, selectedField) => {
    if (selectedGroup) {
      const group = find(groupedContributors, { id: selectedGroup.id })
      return get(group, selectedField)
    }

    return null
  },
)

export const getSelectedGroupSiblings = createSelector(
  [getSelectedGroup, getGroupedContributors, getSelectedField],
  (selectedGroup, groupedContributors, selectedField) => {
    if (selectedGroup) {
      return chain(groupedContributors)
        .filter(createContributorFilter(selectedField))
        .map(group => ({
          ...group,
          value: get(group, selectedField),
        }))
        .orderBy(["name"], ["asc"])
        .value()
    }

    return null
  },
)
